import type { ReactNode } from 'react';

import { NX } from '@/lib/tokens';

/**
 * Override de classes pour les liens texte des écrans auth ("Créer un compte",
 * "Mot de passe oublié", "Retour à la connexion").
 *
 * Ces liens passent par le `Button` partagé (MAN-110) pour hériter de son
 * contrat d'interaction — `type="button"` par défaut, anneau de focus
 * `focus-visible:shadow-focus`, `active:scale` — mais pas de sa boîte : on
 * neutralise hauteur, padding et relief de survol pour retrouver l'aspect d'un
 * lien inline. La typographie (taille/graisse) reste au call site : elle diffère
 * d'un écran à l'autre.
 *
 * Volontairement une constante partagée plutôt qu'un variant `link` dans
 * `Button.tsx` : `Button.test.tsx` verrouille l'invariant « tout variant expose
 * un `hover:shadow-*` » (MAN-110 Task 2), qu'un variant lien contredirait par
 * construction. Déclarer ce variant est une décision du design system, pas de
 * cette tranche d'habillage.
 */
export const AUTH_LINK_BUTTON_CLASS =
  'h-auto p-0 text-nx-primary-text hover:bg-transparent hover:shadow-none';

export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="nx-bg-grid"
      // Sans titlebar système (`decorations: false`), la fenêtre desktop ne se
      // déplace que depuis une drag region — et l'écran de connexion n'en
      // avait aucune. Attribut nu (pas `deep`) : seuls les clics DIRECTS sur
      // ce fond de grille déplacent la fenêtre, la carte de formulaire et ses
      // champs restent hors de portée. Hors Tauri l'attribut est inerte.
      data-tauri-drag-region=""
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        position: 'relative',
      }}
    >
      {/*
        Carte de formulaire : profondeur via les tokens glass/shadow (ADR-021)
        et animation d'entrée.

        Le halo violet de fond (`--nx-halo`, dark uniquement) fait désormais
        partie de `.nx-bg-grid` — cf. MAN-131 : plus besoin de le dupliquer
        ici en dur, il s'applique aux 4 shells qui portent la classe.

        L'animation est portée par la carte et pas par le wrapper racine : ce
        dernier fait 100vh et peint `--nx-bg` via `.nx-bg-grid`. L'animer
        ferait virer toute la page pendant la transition, et son
        `translateY` initial déborderait le document de 16px (scrollbar
        fugace).

        `animate-in` (tailwindcss-animate) ne pose pas de `pointer-events:none` :
        le formulaire reste cliquable/saisissable pendant la transition.
        `prefers-reduced-motion` est neutralisé globalement
        (`styles/global.css:92`), aucune plomberie JS ici.
      */}
      <div
        data-testid="auth-card"
        className="animate-in fade-in slide-in-from-bottom-4 duration-500"
        style={{
          width: '100%',
          maxWidth: 400,
          position: 'relative',
          background: NX.glassBg,
          backdropFilter: NX.glassBlur,
          WebkitBackdropFilter: NX.glassBlur,
          border: `1px solid ${NX.glassBorder}`,
          boxShadow: NX.shadowMd,
          borderRadius: NX.radiusXl,
          padding: '32px 28px',
        }}
      >
        {children}
      </div>
    </div>
  );
}
